/**
 * 边注游戏路单数据处理工具 (牛牛/炸金花)
 *
 * 参考旧项目: game-live/src/utils/gameDetails.js
 *
 * 牛牛(SNN)和炸金花(ZJH)共用相同的处理逻辑和Canvas组件,
 * 只是数据解析和映射表不同
 */

import { BULL_WIN_POSITION_MAP, BULL_ROAD_RESULT_MAP, GOLDEN_FLOWER_PATTERN_MAP, CARD_RANK_MAP } from '@/constants'

/**
 * 边注游戏类型
 */
export type SideGameType = 'SNN' | 'ZJH' // SNN=牛牛, ZJH=炸金花

/**
 * 路单单元格数据
 */
export interface SideGameRoadCell {
  value: string // 原始值 (如: 'a', '0', '1', ..., 'a1', 'b2' 等)
  road: string // 显示文本 (多语言key或原始值)
  win: boolean // 是否赢了
}

/**
 * 路单数据 (列数组)
 */
export type SideGameRoadData = SideGameRoadCell[][]

/**
 * 生成炸金花牌型映射
 *
 * 将字符代码映射到牌型多语言 key 和牌面值
 * 例如: 'a1' -> 'msg113|A' (散牌|A)
 * 格式: "牌型多语言key|牌面值"
 *
 * @returns 炸金花牌型映射对象
 */
export function generateGoldenFlowerCardPatterns(): Record<string, string> {
  const result: Record<string, string> = {}

  for (const [patternKey, chars] of Object.entries(GOLDEN_FLOWER_PATTERN_MAP)) {
    for (let i = 0; i < chars.length; i++) {
      const char = chars[i]
      for (const [rankKey, rankValue] of Object.entries(CARD_RANK_MAP)) {
        const cardKey = char + rankKey
        // 格式: "牌型多语言key|牌面值" 例如: "msg113|A"
        result[cardKey] = `${patternKey}|${rankValue}`
      }
    }
  }

  return result
}

/**
 * 处理边注游戏(牛牛/炸金花)主路数据
 *
 * 参考旧项目: game-live/src/utils/gameDetails.js -> processNnZjhMainRoad
 *
 * 数据格式:
 * - 牛牛: "赢家ID/牛牛结果"  例如: "10/a023" (庄赢, 牛牛/无牛/牛2/牛3)
 * - 炸金花: "赢家ID/牌型"   例如: "10/a1b2c3d4" (庄赢, 4个位置的牌型)
 *
 * @param mainRoad - curShoesData 原始数据
 * @param type - 游戏类型 (SNN=牛牛, ZJH=炸金花)
 * @param columns - 显示列数
 * @returns 处理后的路单数据 (二维数组)
 */
export function processSideGameMainRoad(
  mainRoad: string[] = [],
  type: SideGameType = 'SNN',
  columns: number = 11
): SideGameRoadData {
  // 炸金花牌型映射 (只有炸金花需要)
  const zjhCardMap = type === 'ZJH' ? generateGoldenFlowerCardPatterns() : {}

  // 解析每一局的数据
  const list = mainRoad.map(item => {
    if (!item) return undefined

    // 分割数据: "赢家ID/结果"
    const [whoWin, n] = item.split('/')
    if (!n) return undefined // 如果没有第二部分，跳过这项

    const whatN = n.toLowerCase() // 确保字母都是小写
    let nList: string[]

    if (type === 'ZJH') {
      // 炸金花: 每2个字符一组 (如: 'a1b2c3d4' -> ['a1', 'b2', 'c3', 'd4'])
      const matched = whatN.match(/(.{1,2})/g)
      if (!matched) return undefined // 如果匹配失败，跳过这项
      nList = matched
    } else {
      // 牛牛: 每个字符一个位置 (如: 'a023' -> ['a', '0', '2', '3'])
      nList = whatN.split('')
    }

    // 获取赢家位置列表
    const winList = BULL_WIN_POSITION_MAP[Number(whoWin)] || []

    // 映射每个位置的数据
    return nList.map((i, idx) => {
      const win = winList.includes(idx) // 该位置是否赢了

      // 根据游戏类型获取显示文本
      let road: string
      if (type === 'ZJH') {
        road = zjhCardMap[i] || i // 炸金花: 使用牌型映射
      } else {
        road = BULL_ROAD_RESULT_MAP[i] || i // 牛牛: 使用牛牛点数映射
      }

      return {
        value: i,
        road,
        win,
      }
    })
  })

  // 过滤掉 undefined，取最后 columns 列
  const filteredList = list.filter((item): item is SideGameRoadCell[] => item !== undefined).slice(-columns)

  // 填充占位符到 columns 列 (每列4行: 庄、闲1、闲2、闲3)
  const placeHolderCount = columns - filteredList.length
  const placeHolder: SideGameRoadData = Array.from({ length: placeHolderCount }, () =>
    Array.from({ length: 4 }, () => ({ value: '', road: '', win: false }))
  )

  return [...filteredList, ...placeHolder]
}

/**
 * 边注游戏统计数据类型 (牛牛/炸金花)
 * 两个游戏都有4个位置: 庄、闲1、闲2、闲3
 */
export interface SideGameStats {
  total: number // 总局数
  banker: number // 庄赢次数
  player1: number // 闲1赢次数
  player2: number // 闲2赢次数
  player3: number // 闲3赢次数
}

/**
 * 计算边注游戏统计数据
 *
 * 从原始数据中统计各位置的获胜次数
 *
 * @param mainRoad - curShoesData 原始数据
 * @returns 统计数据
 */
export function calculateSideGameStats(mainRoad: string[] = []): SideGameStats {
  // 初始化统计数据
  const stats: SideGameStats = {
    total: 0,
    banker: 0,
    player1: 0,
    player2: 0,
    player3: 0,
  }

  // 遍历所有局数据,统计获胜次数
  mainRoad.forEach(item => {
    if (!item) return

    // 统计总局数 (有效数据)
    stats.total++

    // 分割数据: "赢家ID/结果"
    const [whoWin] = item.split('/')
    if (!whoWin) return

    // 获取赢家位置列表
    const winList = BULL_WIN_POSITION_MAP[Number(whoWin)] || []

    // 统计各位置获胜次数
    if (winList.includes(0)) stats.banker++ // 庄赢
    if (winList.includes(1)) stats.player1++ // 闲1赢
    if (winList.includes(2)) stats.player2++ // 闲2赢
    if (winList.includes(3)) stats.player3++ // 闲3赢
  })

  return stats
}
